import React from 'react';
import { StandardGrid } from './StandardGrid';
import { Column, Summary, TotalItem, CustomStore, MasterDetail } from './StandardGrid';
import { GridSettingType } from '@apply/constants';
import dxDataGrid, {
    CellClickEvent,
    CellDblClickEvent,
    CellPreparedEvent,
    Column as DevExtremeColumn,
    InitNewRowEvent,
    RowDblClickEvent,
    RowExpandingEvent,
    RowPreparedEvent,
    RowRemovedEvent,
} from 'devextreme/ui/data_grid';
import { EventInfo } from 'devextreme/events';

export { Column, Summary, TotalItem, CustomStore, MasterDetail };

export interface TabularGridProps {
    children?: React.ReactNode;
    gridType?: GridSettingType;
    dataStore?: CustomStore;
    dataSource?: any;
    allowedPageSizes?: number[];
    defaultPageSize?: number;
    onCellPrepared?: (e: CellPreparedEvent<unknown, unknown>) => void;
    onSelectionChanged?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    onCellClick?: (e: CellClickEvent<unknown, unknown>) => void;
    onCellDblClick?: (e: CellDblClickEvent<unknown, unknown>) => void;
    onRowDblClick?: (e: RowDblClickEvent<unknown, unknown>) => void;
    customizeColumns?: (columns: DevExtremeColumn<unknown, unknown>[]) => void;
    onContentReady?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    allowColumnReordering?: boolean;
    onRowPrepared?: (e: RowPreparedEvent<unknown, unknown>) => void;
    onRowRemoved?: (e: RowRemovedEvent<unknown, unknown>) => void;
    onRowExpanding?: (e: RowExpandingEvent<unknown, unknown>) => void;
    onInitNewRow?: (e: InitNewRowEvent<unknown, unknown>) => void;
    wordWrapEnabled?: boolean;
    className?: string;
    width?: number;
    keyExpr?: string;
    allowColumnResizing?: boolean;
    pagerVisible?: boolean;
    showColumnHeaders?: boolean;
    leadPanelEnabled?: boolean;
    loadPanelEnabled?: boolean;
    showBorders?: boolean;
    showRowLines?: boolean;
    rowAlternationEnabled?: boolean;
    selectionMode?: string;
    remoteOperations?: boolean;
    noDataText?: string;
    height?: any;
    tabularColumns: string[];
    detailColumnRowColor?: string;
    alternatingDetailColumnRowColor?: string;
    deemphasizedColumns?: string[];
    deemphasizedBgColor?: string;
    deemphasizedAltBgColor?: string;
    deemphasizedHeaderColor?: string;
}

export const TabularGrid: React.FC<TabularGridProps> = ({
    className = 'tabularStyle',
    tabularColumns,
    detailColumnRowColor = '#E8F0FC',
    alternatingDetailColumnRowColor = '#F6FAFF',
    deemphasizedColumns = [],
    deemphasizedBgColor = '#F3F4F7',
    deemphasizedAltBgColor = '#F8F9FB',
    deemphasizedHeaderColor = '#E9EBEF',
    onCellPrepared,
    children,
    ...remainingProps
}) => {
    const applyColumnStyling = (
        e: CellPreparedEvent,
        lockedColumns: string[] = [],
        bgColor: string,
        altBgColor: string,
        deempColumns: string[],
        deempBgColor: string,
        deempAltBgColor: string,
        deempHeaderColor: string
    ) => {
        const borderColor = '#99A6BF';

        // If no locked columns, nothing special to do
        if (!lockedColumns || lockedColumns.length === 0) {
            return;
        }

        const lastLockedField = lockedColumns[lockedColumns.length - 1];

        // HEADER: right border on last locked column header and style any deemphasizedcolumns
        if (e.rowType === 'header') {
            const field = e.column?.dataField;

            if (field && field === lastLockedField) {
                e.cellElement.style.setProperty('border-right', `1px solid ${borderColor}`, 'important');
            }

            // Deemphasized header background
            if (field && deempColumns.includes(field)) {
                e.cellElement.style.setProperty('background-color', deempHeaderColor, 'important');
            }

            return;
        }

        // DATA ROWS
        if (e.rowType !== 'data') return;

        const field = e.column?.dataField;

        // 1. Locked columns: no background override, but add right border on last locked
        if (field && lockedColumns.includes(field)) {
            if (field === lastLockedField) {
                e.cellElement.style.setProperty('border-right', `1px solid ${borderColor}`, 'important');
            }
            return;
        }

        // 2. Deemphasized columns
        if (field && deempColumns.includes(field)) {
            const isAlt = e.rowIndex % 2 === 1;
            e.cellElement.style.backgroundColor = isAlt ? deempAltBgColor : deempBgColor;
            return;
        }

        // Non-locked columns: apply alternating background
        const isAlt = e.rowIndex % 2 === 1;
        e.cellElement.style.backgroundColor = isAlt ? altBgColor : bgColor;
    };

    const customizeTabularColumns = (columns: DevExtremeColumn[]) => {
        if (tabularColumns?.length) {
            columns.forEach((col) => {
                const field = col.dataField;

                if (field && tabularColumns.includes(field)) {
                    // Prevent resizing
                    col.allowResizing = false;

                    // Freeze column on the left
                    col.fixed = true;
                    col.fixedPosition = 'left';
                }
            });
        }

        // Preserve user-provided customizeColumns
        if (remainingProps.customizeColumns) {
            remainingProps.customizeColumns(columns);
        }
    };

    const handleCellPrepared = (e: CellPreparedEvent) => {
        // Apply your new styling
        applyColumnStyling(
            e,
            tabularColumns,
            detailColumnRowColor, // normal background
            alternatingDetailColumnRowColor, // alternating background
            deemphasizedColumns,
            deemphasizedBgColor,
            deemphasizedAltBgColor,
            deemphasizedHeaderColor
        );

        // Preserve user-provided handler
        if (onCellPrepared) {
            onCellPrepared(e);
        }
    };

    return (
        <StandardGrid
            className={className}
            onCellPrepared={handleCellPrepared}
            customizeColumns={customizeTabularColumns}
            {...remainingProps}
        >
            {children}
        </StandardGrid>
    );
};
